/**
 * Activity picker — a full-screen sheet of rich cards instead of a dropdown.
 *
 * The picker holds NO state between opens. Each card carries the slot it
 * would fill in its own data attributes and selection runs through the one
 * delegated listener in main.ts. That is deliberate: an earlier version
 * closed over `ctx` in a listener attached to the persistent #modal element
 * on every open and never removed it, so after opening on slot A and then
 * slot B a single click ran both closures and wrote the activity into BOTH
 * slots. With nothing captured, that class of bug cannot recur.
 *
 * Two sections: the slot's own offered options first, then everything else
 * the trip knows in this day's city — browsing the whole city is allowed,
 * and the fit badges (fitwarn.ts) say honestly when an option doesn't align
 * with the day (tight before a departure, opens after you leave, closed by
 * arrival). Choosing writes ordinary slot state either way; the definition's
 * curated opts lists are not touched.
 */
import type { Act } from "@trip/shared/types";
import { isFixed } from "@trip/shared/types";
import type { SlotRowComp } from "@trip/shared/engine/schedule";
import { esc, fdur, fmin, safeUrl } from "@trip/shared/engine/time";
import { store } from "../store.js";
import { closeModal } from "./modal.js";
import { fitWarnings, slotFit } from "./fitwarn.js";
import { mountPickerMap, type PickerMapOption } from "./pickermap.js";
import { isFree, priceLabel, reservationOf } from "./price.js";
import { openSheet } from "./sheet.js";
import { onAct } from "./slots.js";
import { currentTrip } from "./times.js";

export interface ActivityPickerContext {
  dayId: string;
  slotIndex: number | string;
  activityIds: string[];
  currentActId?: string;
  slotLabel: string;
}

/* ---------------- city + usage lookups (pure reads of store state) ---------------- */

const cityKey = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .trim();

const sameCity = (a: string, b: string): boolean => {
  const x = cityKey(a);
  const y = cityKey(b);
  return Boolean(x && y && (x === y || x.includes(y) || y.includes(x)));
};

/**
 * Every activity the trip associates with this day's city: the union of every
 * same-city day's slot options, plus any activity whose address names the
 * city. Sentinels (nomodal) never browse.
 */
export function cityActivityIds(dayId: string): string[] {
  const def = store.def;
  const day = def.days.find((d) => d.id === dayId);
  if (!day?.loc) return [];
  const ids = new Set<string>();
  for (const d of def.days) {
    if (!sameCity(d.loc, day.loc)) continue;
    for (const it of d.items) if (!isFixed(it)) for (const id of it.opts) ids.add(id);
  }
  const key = cityKey(day.loc);
  for (const [id, a] of Object.entries(def.act)) {
    if (a.addr && cityKey(a.addr).includes(key)) ids.add(id);
  }
  return [...ids].filter((id) => def.act[id] && !def.act[id].nomodal);
}

export interface UsedAt {
  /** "Sat Sep 13 · 2:30 PM" — first scheduled appearance, in trip order */
  label: string;
  /** additional appearances beyond the first */
  more: number;
}

/**
 * Where each activity is already scheduled, keyed by activity id — what the
 * gold "already on the itinerary" badge shows. The slot being edited is
 * excluded (its pick is "selected", not "used elsewhere").
 */
export function activityUsage(exceptKey: string): Map<string, UsedAt> {
  const def = store.def;
  const trip = currentTrip();
  const uses = new Map<string, { order: number; label: string }[]>();
  for (const [key, st] of Object.entries(store.slots)) {
    if (key === exceptKey || !st || st.dur === 0) continue;
    const a = def.act[st.act];
    if (!a || a.nomodal) continue; // a freetime pick is a gap, not a use
    const us = key.indexOf("_");
    const dayId = key.slice(0, us);
    const rawIdx = key.slice(us + 1);
    const dayIdx = def.days.findIndex((d) => d.id === dayId);
    if (dayIdx === -1) continue;
    const day = def.days[dayIdx];
    const i = /^x\d+$/.test(rawIdx) ? rawIdx : Number(rawIdx);
    const row = trip.days[dayId]?.rows.find((r) => r?.kind === "slot" && r.i === i) as SlotRowComp | undefined;
    const min = row && !row.skipped ? row.startMin : null;
    const list = uses.get(st.act) ?? [];
    list.push({
      order: dayIdx * 10_000 + (min ?? 9_999),
      label: `${day.dow} ${day.date}${min !== null ? ` · ${fmin(min % 1440)}` : ""}`,
    });
    uses.set(st.act, list);
  }
  const out = new Map<string, UsedAt>();
  for (const [actId, list] of uses) {
    list.sort((a, b) => a.order - b.order);
    out.set(actId, { label: list[0].label, more: list.length - 1 });
  }
  return out;
}

/* ---------------- cards ---------------- */

interface CardExtras {
  /** modeled travel minutes into this option (null/undefined = not modeled) */
  cost: number | null | undefined;
  used?: UsedAt;
  warns: string[];
}

function activityCardHTML(id: string, act: Act, ctx: ActivityPickerContext, x: CardExtras): string {
  // price and reservation are independent axes: FREE badge OR the price
  // label (never both), and every card carries book-ahead or walk-up
  const free = isFree(act.price, act.badge);
  const price = free ? "" : priceLabel(act.price, act.cat);
  const selected = id === ctx.currentActId;
  const web = safeUrl(act.web);
  const pinned = Boolean(store.def.geo[id]);
  return `
    <div class="ap-card${selected ? " selected" : ""}${x.used ? " used" : ""}" id="apc_${esc(id)}" role="button" tabindex="0"
      data-action="act-choose" data-act="${esc(id)}" data-day="${esc(ctx.dayId)}" data-i="${esc(String(ctx.slotIndex))}">
      ${act.wiki ? `<div class="ap-photo" data-wiki="${esc(act.wiki)}"></div>` : ""}
      <div class="ap-content">
        <h3>${esc(act.name)}</h3>
        ${
          x.used
            ? `<span class="ap-used" title="Already on the itinerary">📌 ${esc(x.used.label)}${x.used.more ? ` · +${x.used.more} more` : ""}</span>`
            : ""
        }
        ${x.warns.map((w) => `<span class="ap-warnbadge">${esc(w)}</span>`).join("")}
        ${act.notes ? `<p class="ap-desc">${esc(act.notes)}</p>` : ""}
        <div class="ap-chips">
          ${act.durs
            .filter((d) => d > 0)
            .map((d) => `<span class="ap-chip">${fdur(d)}</span>`)
            .join("")}
          ${
            typeof x.cost === "number"
              ? `<span class="ap-chip ap-trav">🚶 ${x.cost} min away</span>`
              : pinned
                ? ""
                : `<span class="ap-chip ap-nopin">📍 not pinned yet</span>`
          }
        </div>
        <div class="ap-meta">
          ${free ? `<span class="ap-badge ap-free">FREE</span>` : ""}
          ${price ? `<span class="ap-price">${esc(price)}</span>` : ""}
          ${act.hours ? `<span class="ap-hours">🕒 ${esc(act.hours.split("·")[0].trim())}</span>` : ""}
          ${
            reservationOf(act.badge) === "book"
              ? `<span class="ap-badge">⚑ Book ahead</span>`
              : reservationOf(act.badge) === "walkup"
                ? `<span class="ap-badge ap-walkup" title="No reservation needed">Walk-up</span>`
                : `<span class="ap-badge ap-unknown" title="Nobody has recorded whether this needs booking">Reservation?</span>`
          }
          ${web ? `<a class="ap-web" href="${web}" target="_blank" rel="noopener" data-action="ext-link">${esc(web.replace(/^https?:\/\/(www\.)?/, "").split("/")[0])} ↗</a>` : ""}
        </div>
      </div>
    </div>`;
}

/**
 * Wikipedia photos, lazily and once per title: cards render immediately, then
 * each distinct wiki title resolves to a thumbnail through the REST summary
 * (the same endpoint the details modal uses; failures stay silently photoless).
 */
const photoCache = new Map<string, string | null>();
function loadCardPhotos(root: HTMLElement): void {
  const holders = [...root.querySelectorAll<HTMLElement>(".ap-photo[data-wiki]")];
  const byTitle = new Map<string, HTMLElement[]>();
  for (const h of holders) {
    const t = h.dataset.wiki!;
    byTitle.set(t, [...(byTitle.get(t) ?? []), h]);
  }
  for (const [title, els] of byTitle) {
    const apply = (url: string | null) => {
      if (!url) return;
      for (const el of els) {
        if (!el.isConnected) continue;
        el.innerHTML = `<img src="${esc(url)}" alt="" loading="lazy">`;
        el.classList.add("has-photo");
      }
    };
    if (photoCache.has(title)) {
      apply(photoCache.get(title)!);
      continue;
    }
    fetch("https://en.wikipedia.org/api/rest_v1/page/summary/" + encodeURIComponent(title.replace(/ /g, "_")))
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const url: string | null = j?.thumbnail?.source ?? j?.originalimage?.source ?? null;
        photoCache.set(title, url);
        apply(url);
      })
      .catch(() => photoCache.set(title, null));
  }
}

/* ---------------- the sheet ---------------- */

export function openActivityPicker(ctx: ActivityPickerContext) {
  const def = store.def;
  const day = def.days.find((d) => d.id === ctx.dayId);
  if (!day) return;
  const trip = currentTrip();
  const comp = trip.days[ctx.dayId];

  // travel minutes per option — a definition slot's own row (or the blank
  // extra's engine info) models the cost of every OFFERED option
  const costRow =
    typeof ctx.slotIndex === "number"
      ? (comp?.rows[ctx.slotIndex] as SlotRowComp | undefined)
      : (comp?.rows.find((r) => r?.kind === "slot" && r.i === ctx.slotIndex) as SlotRowComp | undefined);
  const optionCosts = costRow?.optionCosts ?? comp?.extra?.optionCosts ?? null;
  const costOf = (id: string): number | null | undefined => optionCosts?.find((c) => c.actId === id)?.cost;

  const slotKey = `${ctx.dayId}_${ctx.slotIndex}`;
  const used = activityUsage(slotKey);

  const dayEndMin = def.dayHome[ctx.dayId] && !def.noEnd[ctx.dayId] ? (store.dayEnds[ctx.dayId] ?? def.dayEnd) : null;
  const fit = comp ? slotFit(day, comp, ctx.slotIndex, dayEndMin) : null;
  // a blank extra row has no modeled start; the engine still knows its slack
  if (fit && fit.freeMin === null && typeof ctx.slotIndex === "string" && comp?.extra) {
    fit.freeMin = comp.extra.slackMin;
    fit.until = comp.extra.anchorLabel ? `your ${comp.extra.anchorLabel} departure` : "your back-by target";
  }

  const offered = ctx.activityIds.filter((id) => def.act[id] && !def.act[id].nomodal);
  const offeredSet = new Set(offered);
  const cityExtras = cityActivityIds(ctx.dayId).filter((id) => !offeredSet.has(id));

  const card = (id: string) => {
    const act = def.act[id];
    const cost = costOf(id);
    return activityCardHTML(id, act, ctx, {
      cost,
      used: used.get(id),
      warns: fit ? fitWarnings(act, typeof cost === "number" ? cost : null, fit) : [],
    });
  };

  const pinnedOptions: PickerMapOption[] = [...offered, ...cityExtras]
    .filter((id) => def.geo[id])
    .map((id) => ({ id, act: def.act[id], geo: [def.geo[id][0], def.geo[id][1]], reg: def.geo[id][2] }));

  const modal = openSheet();
  modal.innerHTML = `
    <div class="m-head">
      <div class="m-cat">${esc(day.dow)} ${esc(day.date)}${day.loc ? ` · ${esc(day.loc)}` : ""} — ${esc(ctx.slotLabel)}</div>
      <h3>Choose an activity</h3>
      ${
        pinnedOptions.length
          ? `<div class="ap-seg" role="tablist">
               <button type="button" class="ap-segbtn on" data-seg="list" role="tab" aria-selected="true">☰ List</button>
               <button type="button" class="ap-segbtn" data-seg="map" role="tab" aria-selected="false">🗺 Map</button>
             </div>`
          : ""
      }
      <button class="m-close" data-action="close" aria-label="close">✕</button>
    </div>
    <div class="m-body ap-picker">
      <div class="ap-split${pinnedOptions.length ? "" : " no-map"}">
        <div class="ap-listpane">
          ${
            offered.length
              ? `<div class="ap-sect">Offered in this slot</div>
                 <div class="ap-grid">${offered.map(card).join("")}</div>`
              : `<div class="dir-note">No activities are offered for this slot yet${cityExtras.length ? " — everything nearby is below" : ""}.</div>`
          }
          ${
            cityExtras.length
              ? `<div class="ap-sect">Elsewhere in ${esc(day.loc)}</div>
                 <div class="ap-grid">${cityExtras.map(card).join("")}</div>`
              : ""
          }
          ${offered.length || cityExtras.length ? "" : `<div class="dir-note">Nothing to offer yet — add activities from 🗂 Activities or ✨ Suggest.</div>`}
        </div>
        ${pinnedOptions.length ? `<div class="ap-mappane" id="ap-mappane"></div>` : ""}
      </div>
    </div>`;

  loadCardPhotos(modal);

  if (pinnedOptions.length) {
    const pane = modal.querySelector<HTMLElement>("#ap-mappane")!;
    const unpinnedCount = offered.length + cityExtras.length - pinnedOptions.length;
    let mounted = false;
    const ensureMap = () => {
      if (mounted) return;
      mounted = true;
      mountPickerMap(pane, {
        dayId: ctx.dayId,
        slotIndex: ctx.slotIndex,
        options: pinnedOptions,
        base: def.dayHome[ctx.dayId] ? (def.hotelPts[def.dayHome[ctx.dayId][0]] ?? null) : null,
        prev: costRow?.fromGeo ?? null,
        unpinnedCount,
      });
    };
    // The map pane starts hidden at every width now, so it is always built on
    // the first switch — a Leaflet map created inside a display:none container
    // measures 0x0 and renders grey until invalidateSize, which is exactly the
    // bug the old desktop-splits-immediately path avoided by accident.
    modal.querySelectorAll<HTMLButtonElement>(".ap-segbtn").forEach((b) =>
      b.addEventListener("click", () => {
        modal.querySelectorAll(".ap-segbtn").forEach((x) => {
          x.classList.toggle("on", x === b);
          x.setAttribute("aria-selected", x === b ? "true" : "false");
        });
        modal.querySelector(".ap-split")!.classList.toggle("show-map", b.dataset.seg === "map");
        if (b.dataset.seg === "map") ensureMap();
      })
    );
  }
}

/** Commit a pick. Called by the delegated listener with the card's own data. */
export function choosePickerActivity(dayId: string, slotIndex: number | string, actId: string) {
  closeModal();
  onAct(dayId, slotIndex, actId);
  // let the re-render land, then mark the row that changed
  setTimeout(() => {
    const row = document.getElementById(`row_${dayId}_${slotIndex}`);
    if (row) {
      row.classList.add("just-edited");
      setTimeout(() => row.classList.remove("just-edited"), 2000);
    }
  }, 0);
}
